import { Component, Inject } from "@angular/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
  MatDialogClose,
  MatDialog,
  MatDialogRef,
  MatDialogModule,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { TranslateModule } from "@ngx-translate/core";
import PasswordValidation from "../../../core/password-validators";
import { AppUrlConstants } from "../../../core/constants/AppUrlConstants";
import { GenericCrudService } from "../../../core/services/generic-crud.service";
import { AlertDialogComponent } from "../../../shared/alert-dialog/alert-dialog.component";
import { RolePipe } from "../../../core/pipes/role.pipe";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { RoleEnum } from "../../../core/enums/role.enum";
import { ApplicationDataService } from "../../../core/services/application-data.service";
import { EncryptionDescriptionService } from "../../../core/services/encryption-decryption.service";
import { NgxIntlTelInputModule } from "ngx-intl-tel-input-gg";
import {
  CountryISO,
  PhoneNumberFormat,
  SearchCountryField,
} from "ngx-intl-tel-input-gg";
import { MatCheckbox } from "@angular/material/checkbox";

@Component({
  selector: "app-user-registration-add-edit-dialog",
  standalone: true,
  templateUrl: "./user-registration-add-edit-dialog.component.html",
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogClose,
    TranslateModule,
    RolePipe,
    MatOptionModule,
    MatSelectModule,
    NgxIntlTelInputModule,
    MatCheckbox,
  ],
})
export class UserRegistrationAddEditDialogComponent {
  form: FormGroup;
  roleEnum = RoleEnum.keys();
  segments: any[] = [];
  preferredCountries: CountryISO[] = [
    CountryISO.Pakistan,
    CountryISO.UnitedArabEmirates,
  ];
  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  separateDialCode = true;
  PhoneNumberFormat = PhoneNumberFormat;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<UserRegistrationAddEditDialogComponent>,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private appDataService: ApplicationDataService,
    private crudService: GenericCrudService,
    private cryptService: EncryptionDescriptionService
  ) { }

  ngOnInit() {
    this.form = this.formBuilder.group(
      {
        userId: [""],
        email: [
          "",
          [Validators.required, Validators.maxLength(60), Validators.email],
        ],
        gender: ["", [Validators.required, Validators.maxLength(100)]],
        userName: [
          "",
          [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(30),
            Validators.pattern("^[a-zA-Z0-9.-]*$"),
            (control: AbstractControl) => /^[a-zA-Z]/.test(control.value) ? null : { usernameStart: true }
          ],
        ],
        // userName: ['', [Validators.required, Validators.maxLength(30), Validators.pattern('[a-zA-Z0-9-]*')]],
        name: ["", [Validators.required, Validators.maxLength(100)]],
        role: ["", [Validators.required]],
        password: [
          "",
          [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(30),
            Validators.pattern(
              '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&]{8,}$'
            ),
          ],
        ],
        mobile: [
          "",
          [
            Validators.required,
            Validators.minLength(11),
            Validators.maxLength(20),
          ],
        ],
        reportingTo: [""],
        segmentId: ["", [Validators.required]],
        confirmPassword: [
          "",
          [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(30),
            Validators.pattern(
              '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&]{8,}$'
            ),
          ],
        ],
        canSeeInfo: [false],
        isActive: [true],
        canViewAnonymousInfo: [false],
      },
      {
        validator: PasswordValidation.MatchPassword,
      }
    );
    if (this.data != null) {
      // this.f["mobile"].disable({ onlySelf: true });
      this.f["userName"].disable({ onlySelf: true });
      // this.f["email"].disable({ onlySelf: true });
      // this.f["gender"].disable({ onlySelf: true });
      this.form.patchValue(this.data);
      this.f["password"].clearValidators();
      this.f["confirmPassword"].clearValidators();
    }
    this.getSegments();
  }
  getSegments() {
    this.appDataService.getAllSegments().subscribe({
      next: (res: any) => {
        this.segments = res?.data;
      },
    });
  }
  add() {
    if (this.form.invalid) {
      // If the form is invalid, mark all controls as touched
      // This will trigger validation error messages for all fields
      this.form.markAllAsTouched();
      return;
    }
    let pHash = this.cryptService.encryptWithPublicKey(
      this.f["password"].value
    );
    const formValue = { ...this.form.value };
    formValue.password = pHash;
    // this.f['password'].setValue(pHash);
    this.f["confirmPassword"].setValue("");
    this.crudService
      .add(formValue, AppUrlConstants.USER_URL + AppUrlConstants.ADD)
      .subscribe({
        next: (res: any) => {
          this.dialogRef.close(res?.data);
        },
        error: (e) => {
          this.f["password"].setValue("");
          this.f["confirmPassword"].setValue("");
          if (e?.status == 409) {
            if (e?.error?.errors !== null) {
              for (const property in e.error.errors) {
                this.openAlert(e.error.errors[property]);
              }
            } else {
              this.openAlert(e?.error?.message);
            }
          }
        },
      });
  }
  edit() {
    if (this.form.invalid) {
      // If the form is invalid, mark all controls as touched
      // This will trigger validation error messages for all fields
      this.form.markAllAsTouched();
      return;
    }
    this.crudService
      .edit(this.form.value, AppUrlConstants.USER_URL + AppUrlConstants.EDIT)
      .subscribe({
        next: (res: any) => {
          this.dialogRef.close(res?.data);
        },
        error: (e) => {
          if (e?.status == 409) {
            if (e?.error?.errors !== null) {
              for (const property in e.error.errors) {
                this.openAlert(e.error.errors[property]);
              }
            } else {
              this.openAlert(e?.error?.message);
            }
          }
        },
      });
  }
  openAlert(message: string) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: "OK",
        },
      },
    });
  }
  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }
}
